import { resolveAssetUrl } from '../assets';

/**
 * Icon Registry - Central system for resolving icons from multiple sources
 * 
 * Supported sources:
 * - si: SimpleIcons (brands, services)
 * - mdi: Material Design Icons (infrastructure, general)
 * - carbon: IBM Carbon Icons (cloud, enterprise)
 * - lucide: Lucide Icons (UI, general)
 * - tabler: Tabler Icons (UI, general)
 * - devicon: Devicon (developer tools)
 * - homelab: Dashboard Icons (1800+ homelab/self-hosted service icons)
 * - svgl: SVGL (modern brand & AI logos, full-color — fills Iconify's gaps)
 * - logos: SVG Logos via Iconify (full-color brand/service logos — the reliable
 *          source for AWS/GCP/Azure services and brands Simple Icons has removed)
 * - custom: Custom/uploaded SVGs (future)
 */

export type IconSource = 'si' | 'mdi' | 'carbon' | 'lucide' | 'tabler' | 'devicon' | 'homelab' | 'svgl' | 'logos' | 'custom';

export interface IconResolver {
  /** Returns a URL to the icon */
  resolve(name: string, color: string): string;
  /** Display name for the source */
  displayName: string;
  /** Base URL for browsing available icons */
  browseUrl?: string;
}

/**
 * Registry of icon resolvers by source
 * All resolvers return URLs that can be used in <img> tags
 */
const resolvers: Record<IconSource, IconResolver> = {
  // SimpleIcons - Brand/service icons
  si: {
    displayName: 'Simple Icons',
    browseUrl: 'https://simpleicons.org/',
    resolve: (name, color) => `https://cdn.simpleicons.org/${name}/${color}`,
  },

  // Material Design Icons - Large collection including infrastructure
  mdi: {
    displayName: 'Material Design Icons',
    browseUrl: 'https://pictogrammers.com/library/mdi/',
    resolve: (name, color) => 
      `https://api.iconify.design/mdi/${name}.svg?color=%23${color}`,
  },

  // Carbon Icons - IBM's icon set, great for enterprise/cloud
  carbon: {
    displayName: 'Carbon Icons',
    browseUrl: 'https://carbondesignsystem.com/guidelines/icons/library/',
    resolve: (name, color) => 
      `https://api.iconify.design/carbon/${name}.svg?color=%23${color}`,
  },

  // Lucide Icons - Clean, consistent UI icons
  lucide: {
    displayName: 'Lucide Icons',
    browseUrl: 'https://lucide.dev/icons/',
    resolve: (name, color) => 
      `https://api.iconify.design/lucide/${name}.svg?color=%23${color}`,
  },

  // Tabler Icons - Over 4000 UI icons
  tabler: {
    displayName: 'Tabler Icons',
    browseUrl: 'https://tabler.io/icons',
    resolve: (name, color) => 
      `https://api.iconify.design/tabler/${name}.svg?color=%23${color}`,
  },

  // Devicon - Developer/programming icons
  devicon: {
    displayName: 'Devicon',
    browseUrl: 'https://devicon.dev/',
    resolve: (name, color) => 
      `https://api.iconify.design/devicon/${name}.svg?color=%23${color}`,
  },

  // Dashboard Icons - Homelab/self-hosted service icons (1800+ icons)
  homelab: {
    displayName: 'Dashboard Icons (Homelab)',
    browseUrl: 'https://dashboardicons.com/',
    resolve: (name, _color) => {
      // Dashboard icons are pre-colored SVGs, color parameter is ignored
      // Supports variants: -light (for dark backgrounds) and -dark (for light backgrounds)
      // Icon names use kebab-case (e.g., 'plex', 'home-assistant', 'nextcloud')
      return `https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/${name}.svg`;
    },
  },

  // SVGL - Modern brand & AI logos, full-color (covers newer AI tools Iconify lacks)
  svgl: {
    displayName: 'SVGL',
    browseUrl: 'https://svgl.app/',
    resolve: (name, _color) => {
      // SVGL logos are pre-colored, full-color SVGs, so the color param is ignored.
      // Some ship themed variants via _light/_dark or -light/-dark suffixes; prefer
      // self-colored (non-suffixed) slugs so they read on both light and dark nodes.
      return `https://cdn.jsdelivr.net/gh/pheralb/svgl@main/static/library/${name}.svg`;
    },
  },

  // SVG Logos (Iconify) - full-color brand/service logos. Best source for cloud
  // services (aws-s3, aws-lambda, google-cloud, azure...) and brands Simple Icons
  // has dropped. Pre-colored, so the color param is ignored.
  logos: {
    displayName: 'SVG Logos',
    browseUrl: 'https://icon-sets.iconify.design/logos/',
    resolve: (name, _color) => `https://api.iconify.design/logos/${name}.svg`,
  },

  // Custom icons - placeholder for future user uploads
  custom: {
    displayName: 'Custom Icons',
    resolve: (name, color) => {
      // For now, return a placeholder. In future, this could fetch from storage
      console.warn(`Custom icon "${name}" not implemented yet`);
      return `https://cdn.simpleicons.org/codesandbox/${color}`;
    },
  },
};

/**
 * Parse an icon identifier into source and name
 * 
 * Format: "source:name" or just "name" (defaults to SimpleIcons)
 * 
 * @example
 * parseIconId('si:docker')     // { source: 'si', name: 'docker' }
 * parseIconId('mdi:server')    // { source: 'mdi', name: 'server' }
 * parseIconId('docker')        // { source: 'si', name: 'docker' } (backward compat)
 */
export function parseIconId(iconId: string): { source: IconSource; name: string } {
  if (!iconId) {
    return { source: 'si', name: 'codesandbox' };
  }

  const colonIndex = iconId.indexOf(':');
  
  if (colonIndex === -1) {
    // No prefix = SimpleIcons (backward compatibility)
    return { source: 'si', name: iconId };
  }

  const source = iconId.substring(0, colonIndex) as IconSource;
  const name = iconId.substring(colonIndex + 1);

  // Validate source exists
  if (!resolvers[source]) {
    console.warn(`Unknown icon source "${source}", falling back to SimpleIcons`);
    return { source: 'si', name: iconId };
  }

  return { source, name };
}

/**
 * Resolve an icon identifier to a URL
 * 
 * @param iconId - Icon identifier (e.g., "si:docker", "mdi:server", or just "docker")
 * @param color - Hex color without # (e.g., "2496ED")
 * @returns URL to the icon
 */
export function resolveIconUrl(iconId: string, color: string = '94a3b8'): string {
  // Custom icons store an R2 object key, a full URL (legacy/externally pasted),
  // or an inline data: URI — resolve those rather than treating them as a
  // namespace. R2 keys and legacy absolute URLs from older domains are rebuilt
  // against the current VITE_R2_PUBLIC_URL; external URLs pass through as-is.
  if (/^(https?:|data:)/i.test(iconId)) return resolveAssetUrl(iconId);
  const keyResolved = resolveAssetUrl(iconId);
  if (keyResolved !== iconId) return keyResolved;
  const { source, name } = parseIconId(iconId);
  return resolvers[source].resolve(name, color);
}

/**
 * Get resolver info for a source
 */
export function getResolverInfo(source: IconSource): IconResolver {
  return resolvers[source];
}

/**
 * Get all available icon sources
 */
export function getAvailableSources(): { id: IconSource; displayName: string; browseUrl?: string }[] {
  return Object.entries(resolvers).map(([id, resolver]) => ({
    id: id as IconSource,
    displayName: resolver.displayName,
    browseUrl: resolver.browseUrl,
  }));
}

/**
 * Check if an icon identifier is valid format
 */
export function isValidIconId(iconId: string): boolean {
  if (!iconId || typeof iconId !== 'string') return false;
  if (/^(https?:|data:)/i.test(iconId)) return true; // custom URL / data-URI icon
  const { source, name } = parseIconId(iconId);
  return !!source && !!name && name.length > 0;
}

/**
 * Convert legacy iconSlug to new icon format
 * Used for backward compatibility with existing diagrams
 */
export function legacySlugToIconId(slug: string): string {
  // Legacy slugs are SimpleIcons slugs
  return `si:${slug}`;
}

/**
 * Get the icon identifier from either new `icon` field or legacy `iconSlug`
 * Provides backward compatibility for existing node data
 */
export function getIconId(data: { icon?: string; iconSlug?: string }): string {
  if (data.icon) {
    return data.icon;
  }
  if (data.iconSlug) {
    return legacySlugToIconId(data.iconSlug);
  }
  return 'si:codesandbox'; // fallback
}
